import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { ReleaseFamily, VersionDescriptor } from '@feedyou-ai/feedbot-schema'

import { useIsHostingVersionUpdating } from '../../../../../../../../store/socketServer'
import { Button, ButtonType } from '../../../../../../../Styleguide/Button'

import { useLoadedBotHostingId } from '../../../../../../../../store/app'
import { FieldWrapper } from './FieldWrapper'
import { UpdateToLatestButton } from './UpdateToLatestButton'
import { ChangeSdkVersionForm } from './ChangeSdkVersionForm'
import { useHostingVersionsCatalogueQuery } from '../../../../../../../../features/hostingVersions'
import { useIsArbitraryVersionSwitchingEnabled } from '../hooks/useIsArbitraryVersionSwitchingEnabled'
import { useDeployedHostingVersion } from '../../../../../../../../store/app/hooks/useDeployedHostingVersion'

export const SdkVersionWidget: React.FC = React.memo(() => {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const isFormEnabled = useIsArbitraryVersionSwitchingEnabled()

  const catalogueQueryState = useHostingVersionsCatalogueQuery(!isFormOpen)
  const releaseCatalogue = catalogueQueryState.data?.releases
  const deployedVersion = useDeployedHostingVersion()

  const hostingId = useLoadedBotHostingId()
  const isUpdating = useIsHostingVersionUpdating()

  if (!hostingId) {
    return <FieldWrapper>Not launched yet</FieldWrapper>
  }

  if (!deployedVersion) {
    return <FieldWrapper>Unknown</FieldWrapper>
  }

  return (
    <>
      <FieldWrapper>
        <span css={labelWrapperCss}>{verbalizeVersion(deployedVersion)}</span>

        <span>
          <UpdateToLatestButton />

          {isFormEnabled && !isFormOpen && !catalogueQueryState.isLoading && !isUpdating && (
            <Button
              text="Change version"
              className="e2e-change-version-button"
              css="margin-left: 14px; min-width: unset;"
              onClick={e => {
                e.preventDefault()
                setIsFormOpen(true)
              }}
              onClickWhenDisabled={e => e.preventDefault()}
              buttonType={ButtonType.DEFAULT}
            />
          )}
        </span>
      </FieldWrapper>

      {isFormEnabled && isFormOpen && !isUpdating && releaseCatalogue && (
        <FormFieldWrapper>
          <ChangeSdkVersionForm
            versionCatalogue={releaseCatalogue}
            deployedVersion={deployedVersion}
            onSubmit={() => setIsFormOpen(false)}
          />
        </FormFieldWrapper>
      )}
    </>
  )
})

const FormFieldWrapper = styled(FieldWrapper)`
  margin: 11px 0;
  width: 100%;
  flex-wrap: nowrap;
`

const labelWrapperCss = css`
  // top + bot margin for small viewport when flex wraps
  margin-top: 8px;
  margin-bottom: 8px;
  margin-right: 20px;
  text-wrap: nowrap;
`

const { Master, Feature, Release } = ReleaseFamily

function verbalizeVersion({
  releaseFamily,
  version,
  commit = ''
}: VersionDescriptor<'withCommit'>) {
  const commitDescription = commit && ` (${commit.slice(0, 8)})`

  if (releaseFamily === Master) {
    return `master/latest` + commitDescription
  }

  if (releaseFamily === Feature) {
    return `feature/${version}` + commitDescription
  }

  if (releaseFamily === Release) {
    return 'v' + version
  }

  return 'unknown'
}
